import { Link } from "react-router-dom"
import useDepartmentDoctors from "../hooks/useDepartmentDoctors"
import DoctorCard from "./DoctorCard"
import { Button } from "../ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle, } from "../ui/card"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion"
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Activity,
  Award,
  Calendar,
  Heart,
  MapPin,
  Phone,
  Mail,
  CheckCircle2,
  Users,
  Microscope,
  AlertCircle,
  Clock,
  ArrowRight
} from "lucide-react"

export default function GastroenterologyPage() {
  const { open: openAppointment } = useAppointment();
  const subspecialties = [
    {
      title: "Medical Gastroenterology",
      description: "For liver, pancreas, and intestinal disorders.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Medical-Gastroenterology-scaled.jpg",
    },
    {
      title: "Surgical Gastroenterology",
      description: "Complex GI and hepatobiliary surgeries.",
      image: "/SurgicalGastroenterology.jpg",
    },
    {
      title: "Hepatology",
      description: "Liver disease management and transplant evaluation.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Hepatology-scaled.jpg",
    },
    {
      title: "Endoscopy & Colonoscopy Services",
      description: "Advanced diagnostic and therapeutic procedures.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Endoscopy-scaled.jpg",
    },
    {
      title: "Pancreatic & Biliary Disease Treatment",
      description: "Comprehensive care for pancreas and bile duct disorders.",
      image: "/Pancreatic&BiliaryDiseaseTreatment.jpg",
    },
  ]

  const treatments = [
    {
      title: "Diagnostic & Therapeutic Endoscopy / Colonoscopy",
      description: "Advanced endoscopic procedures for diagnosis and treatment of GI conditions.",
    },
    {
      title: "ERCP",
      description: "Bile duct & Pancreatic duct,Cannulation & Treatment of stones",
    },
    {
      title: "Liver Disease & Cirrhosis Management",
      description: "Comprehensive care for chronic liver conditions.",
    },
     {
      title: "GI Cancers",
      description: "Pancreatic cancers,Stomach cancers,Gall bladder cancers,Colo-rectal cancers",
    },
    {
      title: "Gallbladder & Pancreatic Surgery",
      description: "Surgical interventions for gallbladder and pancreatic disorders.",
    },
    {
      title: "Laparoscopic GI Surgery",
      description: "Minimally invasive surgical techniques for faster recovery.",
    },
    {
      title: "Gastrointestinal Cancer Surgery",
      description: "Specialized surgical care for GI cancers.",
    },
  ]

  const whyChoose = [
    {
      icon: Users,
      title: "Expert Team",
      description: "Team of expert medical and surgical gastroenterologists",
    },
    {
      icon: Microscope,
      title: "Advanced Facilities",
      description: "Advanced endoscopy and imaging facilities",
    },
    {
      icon: Heart,
      title: "Comprehensive Care",
      description: "Comprehensive liver and pancreas care",
    },
    {
      icon: Activity,
      title: "Minimally Invasive",
      description: "Minimally invasive surgical expertise",
    },
    {
      icon: MapPin,
      title: "Accessible Location",
      description: "Accessible location near Miyapur & Bachupally",
    },
  ]
  const doctors = useDepartmentDoctors(["Medical Gastroenterology", "Surgical Gastroenterology"]);

  const articles = [
    { title: "Common Digestive Disorders and Their Symptoms", icon: "🍽️" },
    { title: "How Lifestyle Affects Liver Health", icon: "🫀" },
    { title: "Benefits of Laparoscopic GI Surgery", icon: "🩺" },
    { title: "Nutrition for Gut Health and Immunity", icon: "🌿" },
  ]

  const faqs = [
    {
      question: "What conditions do gastroenterologists treat?",
      answer:
        "They treat disorders of the stomach, intestines, liver, pancreas, and biliary system including acid reflux, IBS, liver disease, gallstones, and GI cancers.",
    },
    {
      question: "Do you offer endoscopy and colonoscopy?",
      answer:
        "Yes, both diagnostic and therapeutic procedures are available. We have state-of-the-art endoscopy suites with experienced gastroenterologists.",
    },
    {
      question: "When is surgical gastroenterology required?",
      answer:
        "For complex conditions like cancers, gallstones, or bowel obstructions requiring surgical intervention. Our surgical gastroenterologists specialize in minimally invasive techniques.",
    },
  ]
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      "name": "MAMS Hospitals Gastroenterology Department",
      "url": "https://mamshospitals.com/gastroenterology",
      "logo": "https://mamshospitals.com/MAMS-Logo.png",
      "image": "https://mamshospitals.com/Gastroenterology.jpg",
      "description": "MAMS Hospitals provides advanced gastroenterology treatment in Hyderabad including endoscopy, colonoscopy, liver disease treatment, GI surgery, ERCP, and pancreatic care.",
      "medicalSpecialty": "Gastroenterology",
      "telephone": "+91-7399999981",
      "priceRange": "$$",

      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Bachupally",
        "addressLocality": "Hyderabad",
        "addressRegion": "Telangana",
        "postalCode": "500090",
        "addressCountry": "IN"
      },

      "availableService": [
        {
          "@type": "MedicalProcedure",
          "name": "Endoscopy & Colonoscopy"
        },
        {
          "@type": "MedicalProcedure",
          "name": "ERCP Procedure"
        },
        {
          "@type": "MedicalProcedure",
          "name": "Liver Disease Treatment"
        },
        {
          "@type": "MedicalProcedure",
          "name": "Gallbladder Surgery"
        },
        {
          "@type": "MedicalProcedure",
          "name": "Laparoscopic GI Surgery"
        }
      ],

      "sameAs": [
        "https://www.facebook.com/MAMSHospitals",
        "https://www.instagram.com/mams_hospitals"
      ]
    },

    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Do you provide endoscopy and colonoscopy services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, MAMS Hospitals offers advanced diagnostic and therapeutic endoscopy and colonoscopy procedures."
          }
        },
        {
          "@type": "Question",
          "name": "Do you treat liver and pancreatic diseases?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, MAMS Hospitals provides comprehensive liver disease treatment, pancreatic care, and hepatology services."
          }
        },
        {
          "@type": "Question",
          "name": "When should I consult a gastroenterologist?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You should consult a gastroenterologist for abdominal pain, acidity, ulcers, liver disorders, digestive issues, or bowel-related symptoms."
          }
        }
      ]
    },

    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Gastroenterology Department - MAMS Hospitals",
      "url": "https://mamshospitals.com/gastroenterology",
      "description": "Best gastroenterology hospital in Hyderabad offering endoscopy, colonoscopy, GI surgery, liver disease treatment, ERCP, and digestive care.",
      "keywords": [
        "Gastroenterology Hospital Hyderabad",
        "Best Gastroenterologist Hyderabad",
        "Endoscopy Hyderabad",
        "Colonoscopy Hyderabad",
        "Liver Specialist Hyderabad",
        "GI Surgery Hyderabad",
        "Gastro Hospital Bachupally"
      ]
    }
  ];
  return (
    <>
    <SEO
  title="Best Gastroenterology Hospital in Hyderabad | Endoscopy & GI Care | MAMS Hospitals"
  description="MAMS Hospitals offers advanced gastroenterology treatment in Hyderabad including endoscopy, colonoscopy, liver disease treatment, GI surgery, ERCP, and pancreatic care."
  keywords="gastroenterology hospital Hyderabad,
best gastroenterologist Hyderabad,
endoscopy Hyderabad,
colonoscopy Hyderabad,
liver specialist Hyderabad,
GI surgery Hyderabad,
ERCP Hyderabad,
digestive disorder treatment Hyderabad,
pancreatic disease treatment Hyderabad,
laparoscopic GI surgery Hyderabad,
hepatology Hyderabad,
gastro hospital Bachupally,
MAMS Hospitals Gastroenterology"
  schema={schema}
/>
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-background py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-block mb-4 px-4 py-1 bg-primary/10 rounded-full">
                <span className="text-primary font-semibold text-sm">Medical & Surgical Gastroenterology</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-primary mb-6">Your Digestive Health, Our Priority</h1>
              <p className="text-lg text-gray-700 mb-8">
                MAMS Hospitals ranks among the Best Gastroenterology Hospitals in Hyderabad, offering both medical and
                surgical gastroenterology services for digestive and liver disorders.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative mt-8 md:mt-0">
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-primary/10 rounded-full blur-3xl"></div>

              <img
                src="/Gastroenterology.jpg"
                alt="Gastroenterology Care"
                className="relative rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/gastroenterology"
      />

              <div className="absolute -bottom-4 left-2 sm:-bottom-6 sm:-left-6 bg-white p-3 sm:p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Award className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-primary">95%</div>
                    <div className="text-sm text-gray-600">Success Rate</div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Sub-Specialties */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Sub-Specialties in Gastroenterology</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive care across all aspects of digestive health with specialized expertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((specialty, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative h-48">
                  <img src={specialty.image} alt={specialty.title} className="w-full h-full object-cover" />
                </div>

                <CardHeader>
                  <CardTitle className="text-xl">{specialty.title}</CardTitle>
                  <CardDescription className="text-base">{specialty.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* Treatments */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Treatments & Procedures</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Advanced treatments for a wide range of gastrointestinal conditions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treatments.map((item, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                    <Activity className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{item.title}</CardTitle>
                  <CardDescription className="text-base">{item.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Why Choose MAMS for Gastroenterology</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Excellence in digestive health care with a patient-centered approach.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => {
              const Icon = item.icon
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl mb-2">{item.title}</CardTitle>
                    <CardDescription className="text-base">{item.description}</CardDescription>
                  </CardHeader>
                </Card>
              )
            })}
          </div>

        </div>
      </section>

      {/* Specialist */}
      {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* FAQs */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-0 max-w-4xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Common questions about gastroenterology care at MAMS Hospitals.</p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-lg">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-gray-700 text-base">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

       {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department </p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  )
}
